import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { auth } from "@/lib/api/auth";
import {
  AuthError,
  resetCsrfToken,
  setAuthErrorHandler,
  updateCsrfToken,
} from "@/lib/api/transport";
import { users } from "@/lib/api/users";
import {
  hasActiveAccess,
  hasPermanentAccess,
  isSubscribed,
} from "@/lib/auth/tier";
import type { SessionPayload, User } from "@/types/api";

const api = { ...auth, ...users };

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";
export type LogoutReason = "expired" | null;

export interface AuthContextValue {
  status: AuthStatus;
  user: User | null;
  logoutReason: LogoutReason;
  login: (pin: string) => Promise<User>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  uploadAvatar: (avatar: File) => Promise<void>;
  setSession: (payload: SessionPayload) => void;
  patchUser: (patch: Partial<User> | ((user: User) => Partial<User>)) => void;
}

const AuthContext = createContext<AuthContextValue>(null!);

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

// Billing status and feature access are deliberately separate. `tier` records
// whether Flagship access came from a trial/reward or a subscription.
// General does not require this entitlement.
export function useIsSubscribed(): boolean {
  const { user } = useContext(AuthContext);
  return isSubscribed({ tier: user?.tier, expiresAt: user?.expires_at });
}

export function useHasToolAccess(): boolean {
  const { user } = useContext(AuthContext);
  if (!user) return false;
  return hasActiveAccess({ tier: user.tier, expiresAt: user.expires_at });
}

/** Admin-granted permanent access; public UI must not label it as lifetime. */
export function useHasPermanentAccess(): boolean {
  const { user } = useContext(AuthContext);
  return hasPermanentAccess({
    tier: user?.tier,
    expiresAt: user?.expires_at,
  });
}

// Rails layout emits <meta name="user-signed-in" content="true|false">
// based on Warden's session at request time. Lets us skip the boot
// /session call for visitors who definitely have no session — a fresh
// homepage hit no longer fires a (futile) auth fetch.
function readSignedInHint(): boolean {
  if (typeof document === "undefined") return false;
  const meta = document.querySelector('meta[name="user-signed-in"]');
  return meta?.getAttribute("content") === "true";
}

function useAuthProvider(): AuthContextValue {
  const initiallySignedIn = readSignedInHint();
  const [status, setStatus] = useState<AuthStatus>(
    initiallySignedIn ? "loading" : "unauthenticated",
  );
  const [user, setUser] = useState<User | null>(null);
  const [logoutReason, setLogoutReason] = useState<LogoutReason>(null);

  const setSession = useCallback((payload: SessionPayload) => {
    updateCsrfToken(payload.csrf_token);
    setUser(payload.user);
    setStatus("authenticated");
    setLogoutReason(null);
  }, []);

  // Boot: only verify via /session when the server-injected hint says
  // a session exists. Skipping the call entirely for fresh visitors
  // means the marketing homepage doesn't fire an auth request and a
  // 401 doesn't litter the network panel.
  useEffect(() => {
    if (!initiallySignedIn) return;
    let cancelled = false;
    api
      .getSession()
      .then((payload) => {
        if (cancelled) return;
        setSession(payload);
      })
      .catch(() => {
        if (cancelled) return;
        resetCsrfToken();
        setUser(null);
        setStatus("unauthenticated");
      });
    return () => {
      cancelled = true;
    };
  }, [initiallySignedIn, setSession]);

  const handleExpired = useCallback(() => {
    resetCsrfToken();
    setUser(null);
    setStatus("unauthenticated");
    setLogoutReason("expired");
  }, []);

  // Only treat 401s as "session expired" while we're actually
  // authenticated. During boot a fresh visitor's GET /session 401s
  // legitimately ("not logged in"), and during the unauthenticated
  // period a stray 401 (e.g. background refetch) shouldn't surface an
  // expired-session banner to someone who never had a session.
  useEffect(() => {
    if (status !== "authenticated") return;
    setAuthErrorHandler(handleExpired);
    return () => setAuthErrorHandler(() => {});
  }, [status, handleExpired]);

  const login = useCallback(
    async (pin: string): Promise<User> => {
      const payload = await api.login(pin);
      setSession(payload);
      return payload.user;
    },
    [setSession],
  );

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem("abg-post-logout-redirect", "/");
      }
      resetCsrfToken();
      setUser(null);
      setStatus("unauthenticated");
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const payload = await api.refreshSession();
      setSession(payload);
    } catch (e) {
      if (e instanceof AuthError) handleExpired();
    }
  }, [handleExpired, setSession]);

  const uploadAvatar = useCallback(
    async (avatar: File) => {
      const payload = await api.updateAvatar(avatar);
      setSession(payload);
    },
    [setSession],
  );

  const patchUser = useCallback(
    (patch: Partial<User> | ((user: User) => Partial<User>)) => {
      setUser((prev) =>
        prev
          ? { ...prev, ...(typeof patch === "function" ? patch(prev) : patch) }
          : prev,
      );
    },
    [],
  );

  return useMemo(
    () => ({
      status,
      user,
      logoutReason,
      login,
      logout,
      refresh,
      uploadAvatar,
      setSession,
      patchUser,
    }),
    [
      status,
      user,
      logoutReason,
      login,
      logout,
      refresh,
      uploadAvatar,
      setSession,
      patchUser,
    ],
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const value = useAuthProvider();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
